/**
 * Milestone Document Providers — the registry that surfaces app-generated ("integrated")
 * documents inside a milestone's Documents branch, alongside manually-uploaded files.
 *
 * WHY THIS EXISTS
 * Many milestones produce a document that is built and exported inside Cortex rather than
 * uploaded by hand — the Pre-Design Research report, the Conceptual Framework presentation,
 * the issued spec/schedule PDFs, etc. Those used to be reachable only from the milestone's
 * top-level ⋯ menu. This registry lets each one appear as its own row in the Documents
 * branch with its own action menu (view / edit / export), which is where a person looks for
 * "the documents for this milestone."
 *
 * HOW TO ADD A NEW INTEGRATED DOCUMENT TYPE  (see docs/MILESTONE_DOCUMENTS.md for the full guide)
 *   1. Append a provider object to PROVIDERS below.
 *   2. `match(c)`  — return true for the milestone deliverable type(s) it applies to.
 *   3. `build(c)`  — return a descriptor (or null to omit):
 *        { key, title, badge, badgeColor, note, actions: [{ label, run, danger? }] }
 *      Each action's `run()` should dispatch an existing CustomEvent (or call a service) —
 *      reuse the same event the milestone ⋯ menu already fires, so behavior stays identical.
 *   4. That's it. The Documents section renders the row + its menu automatically.
 *
 * `c` (the context passed to match/build) is:
 *   { msId, msDelType, msDelTypeId, projectId, isPosted, frozenByKey, msName, projectName }
 *   `msDelTypeId` is the deliverable type's stable Airtable record id — use it in every `match()`.
 *   `msDelType` is the type's current display NAME — use it only for what a person reads (e.g. a
 *   title string), never for matching (see the 2026-09-22 id-conversion pass across the app).
 *   `frozenByKey` maps a provider key (e.g. 'predesign', 'opc', 'fieldobs', 'punchlist') to the
 *   milestone_documents row that was frozen (or, for punch-list, manually uploaded) for it, if one
 *   exists.
 *
 * ASIs are NOT a provider here — an ASI-type milestone's issued PDF lives on the ASI node itself
 * (MilestoneAsiMount, mounted as the first child of Documents), not as a separate row. A dedicated
 * "Issued PDF" row used to duplicate that same link; removed 2026-09-18 (Matt: declutter — the
 * final PDF lives with the ASI, and there's nothing to preview once it's issued).
 *
 * This module is pure UI glue: it dispatches CustomEvents and reads constants only. It never
 * calls an API directly (the events are handled by host components / the outline engine which
 * route through the service layer).
 */

import { PRESENTATION_TYPE_IDS, CONCEPTUAL_FRAMEWORK_TYPE_ID, FIELD_OBSERVATION_MS_TYPE_IDS, PRE_DESIGN_RESEARCH_TYPE_ID, DRB_PRESENTATION_MS_TYPE_IDS, selectionsDocKinds } from '../outline/constants.js'

const SUBSTANTIAL_COMPLETION_TYPE_ID = 'recGHz7VPDGR5WCnm' // Substantial Completion
import { openDocQcTab, openDocQcTabForGenerated } from '../services/documentMarkup.js'

function fire(name, detail) {
  window.dispatchEvent(new CustomEvent(name, { detail }))
}

/**
 * The AlderQC document-markup actions (docs/DOCUMENT_MARKUP.md), shared by every provider whose
 * row can carry them. `frozen` is the milestone_documents row once issued; falsy = not posted yet.
 *
 * "Markup & QC" is offered ALWAYS, issued or not — `openDocQcTabForGenerated` resolves the current
 * working version itself (rendering/looking one up server-side) when nothing's been issued yet, so
 * this never asks Matt to download a preview and re-upload it. It's async (a render can take a
 * moment), so — unlike this file's other actions — it awaits and surfaces a failure via
 * alertDialog rather than a plain window.open.
 *
 * Mark done / Unlock only make sense once there's an actual issued row to lock, so they stay
 * gated on `frozen` — and, per this file's own "never call an API directly" rule, dispatch events
 * for MilestoneDocumentsSection (the host component) to handle through the service layer.
 */
function docLockActions(c, frozen, source) {
  const out = [{
    label: 'Markup & QC',
    run: async () => {
      const res = await openDocQcTabForGenerated({ source, msId: c.msId, projectId: c.projectId, projectName: c.projectName, msName: c.msName, frozen })
      if (res.error) { const { alertDialog } = await import('./Dialog.jsx'); await alertDialog({ title: 'Could not open markup', message: res.error }) }
    },
  }]
  if (frozen && frozen.url && !c.isPosted) {
    out.push(frozen.locked
      ? { label: 'Unlock', run: () => fire('unlock-document', { documentId: frozen.id, msId: c.msId }) }
      : { label: 'Mark done', run: () => fire('mark-document-done', { documentId: frozen.id, msId: c.msId }) })
  }
  return out
}

/**
 * Shared descriptor for a native pdf-lib report (Pre-Design Research, OPC).
 *
 * The issued copy is committed exactly once — when the milestone is posted, the generator freezes
 * it as a milestone_documents row. So we never offer an on-demand "generate issued PDF" (it would
 * risk drifting from the committed copy). Instead:
 *   - Not posted        → Preview (working draft). The issued copy is created when the ms is posted.
 *   - Posted + frozen   → View issued PDF (opens the committed saved copy, not a regeneration).
 *   - Posted, no frozen → fall back to Preview (a ms posted before this report existed).
 */
function reportDescriptor(c, { key, source, title, badgeColor, event, previewLabel }) {
  const frozen = c.frozenByKey && c.frozenByKey[key]
  if (c.isPosted && frozen && frozen.url) {
    return {
      key, title, badge: 'REPORT', badgeColor,
      note: frozen.locked ? 'Issued · Done' : 'Issued',
      actions: [{ label: 'View issued PDF', run: () => window.open(frozen.url, '_blank', 'noopener') }, ...docLockActions(c, frozen, source)],
    }
  }
  return {
    key, title, badge: 'REPORT', badgeColor,
    note: c.isPosted ? 'Issued copy not found' : (frozen && frozen.locked ? 'Issued when posted · Done' : 'Issued when posted'),
    actions: [{ label: previewLabel, run: () => fire(event, { projectId: c.projectId, preview: true }) }, ...docLockActions(c, frozen, source)],
  }
}

/**
 * Selections Package rows (docs/SELECTIONS_PACKAGE_DOCUMENTS.md) — Outline Specifications and
 * Schedules on Architectural Selections; Schedules only on Interior Selections. Unlike the
 * Pre-Design report, the team saves a copy BEFORE posting: "Mark complete" renders the review copy
 * (light-red REVIEW COPY watermark), saves it as the row's one document, and locks it — that's what
 * gets marked up. Unlock to fix in AlderSPEC and mark complete again. Posting requires every row
 * complete, then re-prints each from what was issued.
 *   frozen = the milestone_documents row (source = kind), if any.
 */
function selectionsDescriptor(c, kind) {
  const frozen = c.frozenByKey && c.frozenByKey[kind]
  const isSpec = kind === 'outline-spec'
  const openSpec = () => window.open('/spec?projectId=' + encodeURIComponent(c.projectId || '')
    + '&msId=' + encodeURIComponent(c.msId) + (isSpec ? '' : '&tab=schedules')
    + (c.projectName ? '&projectName=' + encodeURIComponent(c.projectName) : ''), '_blank')
  const params = { milestoneId: c.msId, projectId: c.projectId, projectName: c.projectName || '', kind }
  const complete = !!(frozen && frozen.locked)
  const actions = [{ label: isSpec ? 'Open AlderSPEC' : 'Open AlderSPEC (Schedules)', run: openSpec }]
  // Until the documents list loads we can't tell whether a copy exists — don't offer Mark complete.
  if (c.docsLoading && !c.isPosted) return { key: kind, title: isSpec ? 'Outline Specifications' : 'Schedules', badge: isSpec ? 'SPEC' : 'SCHED', badgeColor: isSpec ? '#1f6f5c' : '#8a5a1f', note: 'Loading…', actions }
  let note
  if (c.isPosted) {
    note = frozen && frozen.url ? 'Issued' : 'No issued copy'
    if (frozen && frozen.url) actions.push({ label: 'View issued PDF', run: () => window.open(frozen.url, '_blank', 'noopener') })
    // Walk the client through the schedules item by item and record their answers (2026-10-26).
    if (!isSpec) actions.push({ label: 'Walk the client through the selections', run: () => fire('open-selections-walkthrough', { msId: c.msId }) })
  } else {
    note = complete ? 'Complete · review copy' : 'Not marked complete'
    if (frozen && frozen.url) actions.push({ label: 'View review copy', run: () => window.open(frozen.url, '_blank', 'noopener') })
    if (!complete) {
      actions.push({
        label: 'Preview',
        run: async () => {
          const { previewSelectionsDoc } = await import('../services/selectionsDocuments.js')
          const r = await previewSelectionsDoc(params)
          if (r.error) { const { alertDialog } = await import('./Dialog.jsx'); await alertDialog({ title: 'Could not preview', message: r.error }) }
        },
      })
      actions.push({ label: 'Mark complete', run: () => fire('selections-doc-complete', { msId: c.msId, kind }) })
    }
  }
  if (frozen && frozen.url) {
    actions.push({
      label: 'Markup & QC',
      run: async () => {
        const res = await openDocQcTabForGenerated({ source: kind, msId: c.msId, projectId: c.projectId, projectName: c.projectName, msName: c.msName, frozen })
        if (res.error) { const { alertDialog } = await import('./Dialog.jsx'); await alertDialog({ title: 'Could not open markup', message: res.error }) }
      },
    })
    if (complete && !c.isPosted) actions.push({ label: 'Unlock', run: () => fire('unlock-document', { documentId: frozen.id, msId: c.msId }) })
  }
  return {
    key: kind, title: isSpec ? 'Outline Specifications' : 'Schedules',
    badge: isSpec ? 'SPEC' : 'SCHED', badgeColor: isSpec ? '#1f6f5c' : '#8a5a1f',
    note, actions,
  }
}

/* ── Provider registry ────────────────────────────────────────────────────── */
// Order here is the order rows appear in the Documents branch (integrated docs render first).
const PROVIDERS = [
  { key: 'outline-spec', match: c => selectionsDocKinds(c.msDelTypeId).includes('outline-spec'), build: c => selectionsDescriptor(c, 'outline-spec') },
  { key: 'schedules',    match: c => selectionsDocKinds(c.msDelTypeId).includes('schedules'),    build: c => selectionsDescriptor(c, 'schedules') },

  // Pre-Design Research report — native pdf-lib report built from in-app parcel research.
  // On milestone post the issued PDF is frozen as a real milestone_documents row (so it also
  // shows up as a manual/legacy row); this entry is the always-available live view + regenerate.
  {
    key: 'predesign',
    match: c => c.msDelTypeId === PRE_DESIGN_RESEARCH_TYPE_ID,
    build: c => reportDescriptor(c, {
      key: 'predesign',
      source: 'predesign-report',
      title: 'Pre-Design Research Report',
      badgeColor: '#c0392b',
      event: 'generate-predesign-report',
      previewLabel: 'Preview report (working draft)',
    }),
  },

  // Owner's Project Criteria — native pdf-lib report built from the project's Owner Criteria record.
  // Like the pre-design report: previewable any time, frozen as a real milestone_documents row when
  // the Pre-design Research milestone is posted (and shown in the Client Hub).
  {
    key: 'opc',
    match: c => c.msDelTypeId === PRE_DESIGN_RESEARCH_TYPE_ID,
    build: c => reportDescriptor(c, {
      key: 'opc',
      source: 'opc-report',
      title: "Owner's Project Criteria",
      badgeColor: '#1a6fa8',
      event: 'generate-opc-report',
      previewLabel: 'Preview criteria (working draft)',
    }),
  },

  // Site Analysis tool — kit-of-parts site diagrams over the site plan (docs/SITE_ANALYSIS.md).
  // Surfaced ONLY as a row on Pre-design Research, the deliverable it belongs to — same home as the
  // research/criteria reports above. A generic "Site analysis…" entry used to also sit in the
  // Documents ⋯ menu on every other milestone type; removed (2026-07) as confusing tool access
  // outside the one place the tool actually belongs — this row is the only way in now. The frozen
  // PDF (source 'site-analysis', committed on post) is never shown as its own separate manual row —
  // it's the SAME diagram this tool produces, so it hangs a "Download PDF" action off this one line
  // instead of appearing twice.
  {
    key: 'siteanalysis',
    match: c => c.msDelTypeId === PRE_DESIGN_RESEARCH_TYPE_ID,
    build: c => {
      const frozen = c.frozenByKey && c.frozenByKey.siteanalysis
      return {
        key: 'siteanalysis',
        title: 'Site Analysis Map',
        badge: 'TOOL',
        badgeColor: '#2e8b8b',
        note: frozen && frozen.locked ? 'Kit-of-parts site diagrams · Done' : 'Kit-of-parts site diagrams',
        actions: [
          {
            label: 'Open site analysis',
            run: () => fire('open-site-analysis-modal', {
              msId: c.msId,
              projectId: c.projectId,
              projectName: c.projectName || '',
            }),
          },
          ...(frozen && frozen.url ? [{
            label: 'Download PDF',
            run: () => window.open(frozen.url, '_blank', 'noopener'),
          }] : []),
          ...docLockActions(c, frozen, 'site-analysis'),
        ],
      }
    },
  },

  // Room Program — a fast-entry front end onto the real Concept -> Unit -> Level -> Room
  // hierarchy (docs/ROOM_PROGRAM.md), surfaced ONLY here (the Site Analysis Map precedent — one
  // home, not a scattered generic entry). No frozen snapshot is auto-created on post: room data
  // keeps evolving through Concept work, so a PDF is compiled on demand instead (the Drawing
  // Sheets precedent — never a stored artifact that can drift).
  {
    key: 'programbuilder',
    match: c => c.msDelTypeId === PRE_DESIGN_RESEARCH_TYPE_ID,
    build: c => ({
      key: 'programbuilder',
      title: 'Room Program',
      badge: 'TOOL',
      badgeColor: '#7a5c9e',
      note: 'Preliminary room list by level',
      actions: [
        {
          label: 'Open Room Program',
          run: () => fire('open-program-builder-modal', {
            msId: c.msId,
            projectId: c.projectId,
            projectName: c.projectName || '',
          }),
        },
      ],
    }),
  },

  // Field Observation tool (Construction Gates Phase B, docs/CONSTRUCTION_GATES_PLAN.md) —
  // checklist-guided site-walk capture on the five gate reviews, Substantial Completion, and the
  // ad-hoc Field Report type. The observation freezes into Documents/Journey only once ISSUED (the
  // studio's QA/QC gate) — before that, or if the milestone type has no observation opened yet,
  // this row still offers "Open field observation" so the tool is reachable from a cold start.
  {
    key: 'fieldobs',
    match: c => FIELD_OBSERVATION_MS_TYPE_IDS.indexOf(c.msDelTypeId) !== -1,
    build: c => {
      const frozen = c.frozenByKey && c.frozenByKey.fieldobs
      return {
        key: 'fieldobs',
        title: 'Field Observation Report',
        badge: 'TOOL',
        badgeColor: '#c07830',
        note: frozen ? 'Issued' : 'Checklist-guided site walk',
        actions: [
          {
            label: 'Open field observation',
            run: () => fire('open-field-observation-modal', {
              msId: c.msId, projectId: c.projectId, projectName: c.projectName || '', msName: c.msName || '',
            }),
          },
          ...(frozen && frozen.url ? [{
            label: 'Download PDF',
            run: () => window.open(frozen.url, '_blank', 'noopener'),
          }] : []),
        ],
      }
    },
  },

  // Punch list (Construction Gates Phase B small win + Phase D core + trimmings) — the interactive
  // punch tool lives on the offline-capable /field surface ("Open punch tool", one list per
  // PROJECT — items captured on site, statuses moved at the studio, GC flags ready-for-review via
  // the contractor portal). This row is also the labeled place to compile the punch list into a
  // real PDF: "Compile punch list PDF" runs it through the letterhead pipeline with a QR code per
  // row deep-linking straight into that item on the contractor portal (api/punch-items.js's
  // buildPunchPdf, via the 'compile-punch-list-pdf' event → PunchListPdfHost.jsx) and freezes it
  // under Substantial Completion's Documents, replacing any prior punch-list doc — whether it was
  // generated here before or manually uploaded (same source:'punch-list' slot either way; manual
  // upload/replace remains as a fallback for a hand-scanned copy via the same
  // 'open-milestone-doc-upload' bridge event other documents use).
  {
    key: 'punchlist',
    match: c => c.msDelTypeId === SUBSTANTIAL_COMPLETION_TYPE_ID,
    build: c => {
      const doc = c.frozenByKey && c.frozenByKey.punchlist
      return {
        key: 'punchlist',
        title: 'Punch List',
        badge: 'PUNCH',
        badgeColor: '#b3402f',
        note: doc ? 'Attached' : 'Field punch tool + compiled PDF',
        actions: [
          {
            label: 'Open AlderFIELD (punch list)',
            run: () => window.open('/field?punch=' + encodeURIComponent(c.projectId || '')
              + (c.projectName ? '&projectName=' + encodeURIComponent(c.projectName) : ''), '_blank', 'noopener'),
          },
          {
            label: 'Preview compiled PDF',
            run: () => fire('generate-punch-list-pdf', { projectId: c.projectId, msName: c.msName || '' }),
          },
          {
            label: doc ? 'Recompile & attach PDF' : 'Compile & attach PDF',
            run: () => fire('compile-punch-list-pdf', { msId: c.msId, projectId: c.projectId, msName: c.msName || '' }),
          },
          ...(doc && doc.url ? [{ label: 'View attached punch list PDF', run: () => window.open(doc.url, '_blank', 'noopener') }] : []),
          {
            label: 'Attach a different PDF (manual)',
            run: () => fire('open-milestone-doc-upload', { msId: c.msId, presetName: 'Punch List', presetSource: 'punch-list' }),
          },
        ],
      }
    },
  },

  // Conceptual Framework = sketch ribbons + the client inspiration board (Matt, 2026-10-26). The
  // ribbons are their own rows (MilestoneDocumentsSection); this row is the board's door.
  {
    key: 'inspiration',
    match: c => c.msDelTypeId === CONCEPTUAL_FRAMEWORK_TYPE_ID,
    build: c => ({
      key: 'inspiration',
      title: 'Inspiration board',
      note: 'Built with the client',
      actions: [
        { label: 'Open inspiration board', run: () => fire('open-inspiration-board-team', { projectId: c.projectId, projectName: c.projectName, tab: 'all' }) },
      ],
    }),
  },

  // Design presentations — the /present surface (IDP, ICP, IDR; Conceptual Framework left out 2026-10-26).
  // This is both the view and the edit surface. Because the deliverable is a live web page, it
  // can't be marked up directly: "Make review copy for markup" photographs it (as the client sees
  // it) into a NEW dated PDF and opens that in AlderQC (docs/DOCUMENT_MARKUP.md "Presentation
  // review copies"). Every earlier copy stays listed here with its own markup — the frozen record
  // of each review round. Handled by MilestoneDocumentsSection via the event below.
  {
    key: 'presentation',
    match: c => PRESENTATION_TYPE_IDS.indexOf(c.msDelTypeId) !== -1,
    build: c => {
      const copies = c.reviewCopies || []
      return {
        key: 'presentation',
        title: c.msDelType + ' Presentation',
        badge: 'PRES',
        badgeColor: '#7c3aed',
        note: copies.length ? 'Interactive deliverable · ' + copies.length + (copies.length === 1 ? ' review copy' : ' review copies') : 'Interactive deliverable',
        actions: [
          { label: 'Open presentation', run: () => fire('open-presentation', { msId: c.msId }) },
          { label: 'Make review copy for markup', run: () => fire('make-presentation-review-copy', { msId: c.msId }) },
          ...copies.slice(0, 8).map(d => ({
            label: 'Mark up ' + String(d.name || 'review copy').replace(/^.*Review Copy · /, 'review copy · '),
            run: () => openDocQcTab(d, { projectId: c.projectId, projectName: c.projectName, milestoneName: c.msName }),
          })),
        ],
      }
    },
  },

  // Design Review presentation (docs/DRB_PRESENTATION.md) — a 16:9 slide PDF for the planner to put
  // on screen at the hearing: the project's metrics plus its newest DAPC drawings, built fresh from
  // the database each time. Every copy is a dated Documents row (source 'drb-presentation'), listed
  // here newest first. Handled by MilestoneDocumentsSection via the event below.
  {
    key: 'drb',
    match: c => DRB_PRESENTATION_MS_TYPE_IDS.indexOf(c.msDelTypeId) !== -1,
    build: c => {
      const copies = c.drbCopies || []
      const when = d => String(d.name || '').replace(/^.*· /, '')
      return {
        key: 'drb',
        title: 'DRB Presentation',
        badge: 'DRB',
        badgeColor: '#07272D',
        note: copies.length ? 'Slides for the hearing · last made ' + when(copies[0]) : 'Slides for the hearing · metrics and drawings',
        actions: [
          { label: copies.length ? 'Make a new copy…' : 'Make presentation…', run: () => fire('make-drb-presentation', { msId: c.msId }) },
          ...copies.slice(0, 6).map((d, i) => ({
            label: (i === 0 ? 'Open latest · ' : 'Open · ') + when(d),
            run: () => window.open(d.url, '_blank'),
          })),
        ],
      }
    },
  },
]

/**
 * Build the list of integrated document descriptors for a milestone.
 * @param {{ msId: string, msDelType: string, projectId: string, isPosted: boolean }} c
 * @returns {Array<{ key, title, badge, badgeColor, note, actions }>}
 */
export function getIntegratedDocs(c) {
  const out = []
  for (const p of PROVIDERS) {
    try {
      if (p.match(c)) {
        const d = p.build(c)
        if (d) out.push(d)
      }
    } catch (_) { /* a misbehaving provider must never break the Documents branch */ }
  }
  return out
}
